import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Radio,
  Result,
  Space,
  Tag,
  Typography,
  notification,
} from 'antd';
import { CheckCircleFilled, CloseCircleFilled, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { usePermissions } from '../../assets/utils/usePermissions';
import DashboardSection from '../dashboard/components/DashboardSection';

const { Text, Paragraph, Title } = Typography;

const PROVIDER_LABELS = {
  termii: 'Termii',
  trustmail: 'TrustMail',
  twilio: 'Twilio',
  nexmo: 'Nexmo / Vonage',
};

const SmsProviderSettingsPage = () => {
  const { hasAnyPermission } = usePermissions();
  const allowed = useMemo(
    () => hasAnyPermission(['admin.*', 'admin.system_config']),
    [hasAnyPermission],
  );

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [providers, setProviders] = useState([]);
  const [activeProvider, setActiveProvider] = useState(null);
  const [selected, setSelected] = useState(null);
  const [meta, setMeta] = useState({ updated_by: null, updated_at: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await lib.getSmsProviderSettings();
      if (resp?.data?.error) return;
      const data = resp?.data?.data || {};
      setProviders(data.available_providers || []);
      setActiveProvider(data.active_provider ?? null);
      setSelected(data.active_provider ?? null);
      setMeta({ updated_by: data.updated_by, updated_at: data.updated_at });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const save = async () => {
    setSaving(true);
    try {
      const resp = await lib.updateSmsProviderSettings({ active_provider: selected });
      if (resp?.data?.error) {
        notification.error({ message: resp.data.message || 'Failed to update SMS provider' });
        return;
      }
      notification.success({
        message: resp?.data?.message || 'SMS provider setting saved',
        description: selected
          ? `All outgoing SMS now go through ${PROVIDER_LABELS[selected] || selected}.`
          : 'Override cleared — back to automatic routing (Termii for Nigerian numbers, Twilio for international).',
      });
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) {
    return (
      <Result
        status="403"
        title="Admin only"
        subTitle="Switching the active SMS provider requires admin.* or admin.system_config."
      />
    );
  }

  const dirty = selected !== activeProvider;

  return (
    <div style={{ padding: 16, maxWidth: 640, margin: '0 auto' }}>
      <Title level={3} style={{ marginTop: 0 }}>SMS provider</Title>
      <Paragraph type="secondary">
        Pin every outgoing SMS to one provider, or leave on automatic routing (Termii for
        Nigerian numbers, Twilio for international, falling back to whichever provider is
        configured). Only providers with API credentials on this environment can be selected.
      </Paragraph>

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Takes effect on the next SMS sent"
        description="There is no staged rollout — save applies immediately to every send after this."
      />

      <DashboardSection
        id="sms-provider-settings"
        title="Active provider"
        subtitle={meta.updated_at ? `Last changed ${meta.updated_at}${meta.updated_by ? ` by staff #${meta.updated_by}` : ''}` : 'No override set — using automatic routing'}
        collapsible={false}
      >
        <Radio.Group
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Radio value={null}>
              <Space>
                <Text strong>Automatic (no override)</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>Termii → Nigerian, Twilio → international</Text>
              </Space>
            </Radio>
            {providers.map((p) => (
              <Radio key={p.name} value={p.name} disabled={!p.configured}>
                <Space>
                  <Text strong>{PROVIDER_LABELS[p.name] || p.name}</Text>
                  {p.configured ? (
                    <Tag icon={<CheckCircleFilled />} color="success">Configured</Tag>
                  ) : (
                    <Tag icon={<CloseCircleFilled />} color="default">No API key set</Tag>
                  )}
                </Space>
              </Radio>
            ))}
          </Space>
        </Radio.Group>

        <Space wrap style={{ marginTop: 20 }}>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Reload
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={save}
            loading={saving}
            disabled={!dirty}
          >
            Save
          </Button>
        </Space>
      </DashboardSection>
    </div>
  );
};

export default SmsProviderSettingsPage;
